import React, { useEffect, useState, useContext } from "react";
import ProfileCard from "../../../common/ProfileCard/ProfileCard";
import DietPlanTab from "../../../features/diet/workflow/plan/view/DietPlanTab";
import ActivityTab from "../../../features/workouts/workflow/profile/view/ActivityTab";
import NotesTab from "../../../features/notes/workflow/profile/view/NotesTab";
import HormonalWellnessTab from "../../../features/hormones/workflow/HormonalWellnessTab";
import GutHealthTab from "../../../features/gut/workflow/GutHealthTab";
import UserAnalytics from "./userAnalytics/UserAnalytics";
import Subscriptions from "./subscriptions/Subscriptions";
import { useParams } from "react-router-dom";
import { ApiContext } from "../../../context/MyContext";
import { Toaster } from "../../../common/Toaster/Toaster";
import { hasButtonPermission } from "../../../utils/accessControl";
import { serializeUserNotesKey } from "../../../utils/listCache/caches";

const UserInfoDashboard = () => {
  const [selectedTab, setSelectedTab] = useState("Nutrition");
  const { id } = useParams();
  const { userId, setUserId, employeeData, departmentsWithRoles } = useContext(ApiContext);
  const canViewSubscriptionsTab = hasButtonPermission("view-subscriptions-tab", "user-dashboard", employeeData, departmentsWithRoles);
  const canViewHormonesTab = hasButtonPermission("view-hormonal-wellness-tab", "user-dashboard", employeeData, departmentsWithRoles);
  const canViewGutTab = hasButtonPermission("view-gut-health-tab", "user-dashboard", employeeData, departmentsWithRoles);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  useEffect(() => {
    if (id && userId !== id) {
      setUserId(id);
      localStorage.setItem("userId", JSON.stringify(id));
    }
  }, [id, userId, setUserId]);

  // If Subscriptions tab is hidden and was selected, switch to Nutrition
  useEffect(() => {
    if (
      (!canViewSubscriptionsTab && selectedTab === "Subscriptions") ||
      (!canViewHormonesTab && selectedTab === "Hormonal Wellness") ||
      (!canViewGutTab && selectedTab === "Gut Health")
    ) {
      setSelectedTab("Nutrition");
    }
  }, [canViewSubscriptionsTab, canViewHormonesTab, canViewGutTab, selectedTab]);

  const showAlert = (message) => {
    setAlertMessage?.(message);
    setAlertType("success");
    setTimeout(() => setAlertMessage(""), 3000);
  };

  if (!userId) return <div>Loading...</div>;

  return (
    <div className="flex-1 flex flex-col items-center">
      {alertMessage && <Toaster message={alertMessage} type={alertType} />}
      <div className="w-full px-2 py-4 overflow-auto">
        <ProfileCard profileId={id} enableNotesTab />
        <div className="flex space-x-4 mt-4 bg-white p-4 rounded-lg">
          <button
            type="button"
            className={`py-2.5 px-5 me-2 mb-1 text-gray-600 focus:outline-none font-small ${
              selectedTab === "Nutrition" ? "bg-input-bg text-color-text" : ""
            }`}
            onClick={() => setSelectedTab("Nutrition")}
          >
            Nutrition
          </button>
          <button
            type="button"
            className={`py-2.5 px-5 me-2 mb-1 text-gray-600 focus:outline-none font-small ${
              selectedTab === "Activity" ? "bg-input-bg text-color-text" : ""
            }`}
            onClick={() => setSelectedTab("Activity")}
          >
            Activity
          </button>
          <button
            type="button"
            className={`py-2.5 px-5 me-2 mb-1 text-gray-600 focus:outline-none font-small ${
              selectedTab === "User Analytics"
                ? "bg-input-bg text-color-text"
                : ""
            }`}
            onClick={() => setSelectedTab("User Analytics")}
          >
            User Analytics
          </button>
          <button
            type="button"
            className={`py-2.5 px-5 me-2 mb-1 text-gray-600 focus:outline-none font-small ${
              selectedTab === "Notes" ? "bg-input-bg text-color-text" : ""
            }`}
            onClick={() => setSelectedTab("Notes")}
          >
            Notes
          </button>
          {canViewHormonesTab && (
            <button
              type="button"
              className={`py-2.5 px-5 me-2 mb-1 text-gray-600 focus:outline-none font-small ${
                selectedTab === "Hormonal Wellness" ? "bg-input-bg text-color-text" : ""
              }`}
              onClick={() => setSelectedTab("Hormonal Wellness")}
            >
              Hormonal Wellness
            </button>
          )}
          {canViewGutTab && (
            <button
              type="button"
              className={`py-2.5 px-5 me-2 mb-1 text-gray-600 focus:outline-none font-small ${
                selectedTab === "Gut Health" ? "bg-input-bg text-color-text" : ""
              }`}
              onClick={() => setSelectedTab("Gut Health")}
            >
              Gut Health
            </button>
          )}
          {canViewSubscriptionsTab && (
            <button
              type="button"
              className={`py-2.5 px-5 me-2 mb-1 text-gray-600 focus:outline-none font-small ${
                selectedTab === "Subscriptions" ? "bg-input-bg text-color-text" : ""
              }`}
              onClick={() => setSelectedTab("Subscriptions")}
            >
              Subscriptions
            </button>
          )}
        </div>
        <div className="w-full min-w-0 bg-app-bg rounded-lg p-10 shadow-lg flex items-stretch">
          {selectedTab === "Nutrition" && (
            <DietPlanTab profileId={id} showAlert={showAlert} />
          )}
          {selectedTab === "Activity" && (
            <ActivityTab profileId={id} showAlert={showAlert} />
          )}
          {selectedTab === "User Analytics" && (
            <UserAnalytics profileId={id} showAlert={showAlert} />
          )}
          {selectedTab === "Notes" && (
            <NotesTab
              key={id}
              profileId={id}
              cacheKey={serializeUserNotesKey(id)}
            />
          )}
          {canViewHormonesTab && selectedTab === "Hormonal Wellness" && (
            <HormonalWellnessTab key={id} profileId={id} />
          )}
          {canViewGutTab && selectedTab === "Gut Health" && (
            <GutHealthTab key={id} profileId={id} />
          )}
          {canViewSubscriptionsTab && selectedTab === "Subscriptions" && (
            <Subscriptions profileId={id} showAlert={showAlert} />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInfoDashboard;
